import * as React from "react";
import {
  Grid,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import { AuthenticationContext, ImagesContext } from "../Context";
import { getHeaders } from "../utils/HeaderHelper";
import { useFlash } from "../FlashContext";
import { useCustomerPermission } from "../hooks/useCustomerPermission";
import AddSupplyRequestModal from "./AddSupplyRequestModal";

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "new", label: "New" },
  { value: "completed", label: "Completed" },
  { value: "canceled", label: "Canceled" },
];

const tableHeaderCellSx = {
  fontFamily: "QuicksandBold",
  color: "#1E1E1E",
  fontSize: 12,
  fontStyle: "normal",
  fontWeight: 700,
};

const centeredTableHeaderCellSx = {
  ...tableHeaderCellSx,
  textAlign: "center",
};

const cellSx = {
  color: "#1E1E1E",
  fontFamily: "QuicksandMedium",
  fontSize: "13px",
  lineHeight: "18px",
};

const baseBadgeSx = {
  display: "inline-flex",
  width: 90,
  padding: "0 8px",
  justifyContent: "center",
  alignItems: "center",
  flexShrink: 0,
  borderRadius: "10px",
  fontFamily: "QuicksandMedium",
  fontSize: "10px",
  fontWeight: 600,
  lineHeight: "20px",
  letterSpacing: "0.4px",
};

const statusStylesByValue: Record<string, any> = {
  new: { ...baseBadgeSx, background: "#FFF0BE", color: "#B08800" },
  pending: { ...baseBadgeSx, background: "#FFF0BE", color: "#B08800" },
  completed: { ...baseBadgeSx, background: "#EDF7EE", color: "#07BC2A" },
  shipped: { ...baseBadgeSx, background: "#E3F2FD", color: "#1565C0" },
  canceled: { ...baseBadgeSx, background: "#FFD9D9", color: "#F00" },
  cancelled: { ...baseBadgeSx, background: "#FFD9D9", color: "#F00" },
};

interface Props {
  encounterId: string | number;
  patientId: string | number;
  highlightSupplyRequestId?: string | number | null;
}

const EncounterSuppliesPanel: React.FC<Props> = ({
  encounterId,
  patientId,
  highlightSupplyRequestId,
}) => {
  const highlightedRowRef = React.useRef<HTMLTableRowElement | null>(null);
  const authenticationSetting = React.useContext<any>(AuthenticationContext);
  const images = React.useContext<any>(ImagesContext);
  const { showToast } = useFlash();

  // Organization-level privilege gating, identical pattern to Nav and ProviderRoutes.
  const { permitted: hasSuppliesPrivilege, loading: permissionLoading } =
    useCustomerPermission("Supplies Requests");

  const [requests, setRequests] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
  const [supplyOptions, setSupplyOptions] = React.useState<string[]>([]);
  const [supplyItemObjects, setSupplyItemObjects] = React.useState<any[]>([]);
  const [patientPayerNames, setPatientPayerNames] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (!patientId) return;
    fetch(`/data_fetching/patient_payer/${patientId}`, {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data?.success && Array.isArray(data?.resource?.payer_names)) {
          setPatientPayerNames(data.resource.payer_names);
        } else {
          setPatientPayerNames([]);
        }
      })
      .catch(() => setPatientPayerNames([]));
  }, [patientId, authenticationSetting.csrfToken]);

  // Status dropdown state
  const [statusAnchorEl, setStatusAnchorEl] =
    React.useState<HTMLElement | null>(null);
  const [activeStatusRowId, setActiveStatusRowId] = React.useState<
    number | null
  >(null);
  const [statusUpdatingId, setStatusUpdatingId] = React.useState<number | null>(
    null,
  );

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = React.useState<boolean>(false);
  const [selectedRequest, setSelectedRequest] = React.useState<any>(null);

  const hasPersistedEncounter = Boolean(encounterId);
  const [sortObject, setSortObject] = React.useState({
    field: "request_date",
    direction: "descending",
  });

  const setSortOrder = (sortBy: string, direction: string) => {
    setSortObject({
      field: sortBy,
      direction,
    });
  };

  const fetchSupplyRequests = React.useCallback(() => {
    if (!encounterId) {
      setRequests([]);
      return;
    }
    setLoading(true);
    const queryParams = new URLSearchParams();
    queryParams.append("encounter_billing_id", String(encounterId));
    queryParams.append("per_page", "100");
    queryParams.append("status[]", "new");
    queryParams.append("status[]", "completed");
    queryParams.append("status[]", "canceled");
    queryParams.append("sort_by", sortObject.field);
    queryParams.append("sort_direction", sortObject.direction);

    fetch(
      `/data_fetching/supply_requests.json?${queryParams.toString()}`,
      {
        method: "GET",
        headers: getHeaders(authenticationSetting.csrfToken),
      },
    )
      .then((res) => res.json())
      .then((data) => {
        if (data?.success && data?.resource?.supply_requests) {
          setRequests(data.resource.supply_requests);
        } else {
          setRequests([]);
        }
      })
      .catch((err) => {
        showToast?.({
          title: "Supply Requests Load Error",
          text: err?.message || "Failed to load supply requests.",
          type: "error",
        });
        setRequests([]);
      })
      .finally(() => setLoading(false));
  }, [encounterId, authenticationSetting.csrfToken, showToast, sortObject]);

  React.useEffect(() => {
    if (hasSuppliesPrivilege && hasPersistedEncounter) {
      fetchSupplyRequests();
    }
  }, [hasSuppliesPrivilege, hasPersistedEncounter, fetchSupplyRequests]);

  // Scroll to and highlight the supply request that was linked to from a
  // report (e.g. "View Supply Request" on the Supply Requests Billing Report).
  React.useEffect(() => {
    if (!highlightSupplyRequestId || requests.length === 0) return;
    if (highlightedRowRef.current) {
      highlightedRowRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [highlightSupplyRequestId, requests]);

  React.useEffect(() => {
    fetch("/data_fetching/supply_items.json", {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((res) => res.json())
      .then((data) => {
        const items: any[] = data.items || [];
        setSupplyItemObjects(items);
        setSupplyOptions(items.map((i: any) => i.name));
      })
      .catch(() => {
        setSupplyItemObjects([]);
        setSupplyOptions([]);
      });
  }, [authenticationSetting.csrfToken]);

  const handleOpenStatusMenu = (
    event: React.MouseEvent<HTMLElement>,
    rowId: number,
  ) => {
    event.stopPropagation();
    setStatusAnchorEl(event.currentTarget);
    setActiveStatusRowId(rowId);
  };

  const handleCloseStatusMenu = () => {
    setStatusAnchorEl(null);
    setActiveStatusRowId(null);
  };

  const handleStatusUpdate = async (rowId: number, newStatus: string) => {
    // Optimistically capture current request and close menu immediately
    const currentRow = requests.find((r) => r.id === rowId);
    if (currentRow && currentRow.status === newStatus) {
      handleCloseStatusMenu();
      return;
    }
    setStatusUpdatingId(rowId);
    try {
      const response = await fetch(
        `/data_fetching/supply_requests/${rowId}.json`,
        {
          method: "PATCH",
          headers: getHeaders(authenticationSetting.csrfToken),
          body: JSON.stringify({
            supply_request: { status: newStatus },
          }),
        },
      );
      const data = await response.json();
      if (data?.success) {
        setRequests((prev) =>
          prev.map((r) =>
            r.id === rowId
              ? { ...r, ...(data.resource || {}), status: newStatus }
              : r,
          ),
        );
        showToast?.({
          title: "Status Updated",
          text: "Supply request status updated successfully.",
          type: "success",
        });
        fetchSupplyRequests();
      } else {
        showToast?.({
          title: "Status Update Failed",
          text: data?.message || "Could not update supply request status.",
          type: "error",
        });
      }
    } catch (err: any) {
      showToast?.({
        title: "Status Update Error",
        text: err?.message || "Failed to update supply request status.",
        type: "error",
      });
    } finally {
      setStatusUpdatingId(null);
      handleCloseStatusMenu();
    }
  };

  const handleOpenEditModal = (row: any) => {
    setSelectedRequest(row);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedRequest(null);
  };

  const renderStatusBadge = (row: any) => {
    const normalized = (row?.status || "new").toLowerCase();
    const sx = statusStylesByValue[normalized] || statusStylesByValue.new;
    const label = normalized.charAt(0).toUpperCase() + normalized.slice(1);
    const isOpen =
      Boolean(statusAnchorEl) && activeStatusRowId === row.id;
    const isUpdating = statusUpdatingId === row.id;
    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          cursor: isUpdating ? "not-allowed" : "pointer",
          opacity: isUpdating ? 0.6 : 1,
        }}
        onClick={(e) => {
          if (isUpdating) return;
          handleOpenStatusMenu(e, row.id);
        }}
        role="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {isOpen ? (
          <KeyboardArrowUpIcon sx={{ fontSize: 22, color: "#000" }} />
        ) : (
          <KeyboardArrowDownIcon sx={{ fontSize: 22, color: "#000" }} />
        )}
        <Box sx={sx}>{label}</Box>
      </Box>
    );
  };

  // Hide the entire section if the customer (org) doesn't have the privilege.
  if (permissionLoading) return null;
  if (!hasSuppliesPrivilege) return null;

  const addLinkDisabled = !hasPersistedEncounter;

  return (
    <>
      <Grid container className="panel-container" borderRadius="4px">
        <Grid item xs={12}>
          <Grid container className="panel-show-container">
            <Grid container className="panel-information-container">
              <Grid container direction="row" className="admin-header">
                <Grid item xs={12} className="box-header">
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    paddingX={3}
                    paddingY={1}
                    alignItems="center"
                    display="flex"
                  >
                    <Grid item>
                      <p className="panel-header">Supply Request</p>
                    </Grid>
                    <div className="row">
                      <Tooltip
                        title={
                          addLinkDisabled
                            ? "Save the encounter first to add supply requests."
                            : ""
                        }
                        arrow
                        disableHoverListener={!addLinkDisabled}
                      >
                        <span>
                          <Link
                            className="action-link row"
                            onClick={(event: React.MouseEvent) => {
                              if (addLinkDisabled) {
                                event.preventDefault();
                                return;
                              }
                              setIsModalOpen(true);
                            }}
                            sx={{
                              cursor: addLinkDisabled
                                ? "not-allowed"
                                : "pointer",
                              opacity: addLinkDisabled ? 0.5 : 1,
                              pointerEvents: addLinkDisabled ? "none" : "auto",
                              textDecoration: "none",
                            }}
                          >
                            <img
                              src={images?.add_icon}
                              alt=""
                              className="action-icon-image"
                              style={{ width: 31.466, maxHeight: 32 }}
                            />
                            <span
                              className="app-user-text disable-pointer"
                              style={{ marginLeft: "8px" }}
                            >
                              Add Supply Request
                            </span>
                          </Link>
                        </span>
                      </Tooltip>
                    </div>
                  </Stack>
                </Grid>
              </Grid>

              <Grid container className="grey-container">
                <Grid container className="tabs-container">
                  <Grid
                    item
                    xs={12}
                    className="panel-body tab-body-container"
                    padding={0}
                    sx={{
                      border: "0px !important",
                      paddingBottom: "0px !important",
                    }}
                  >
                    {loading ? (
                      <p
                        style={{
                          textAlign: "center",
                          fontSize: "13px",
                          fontWeight: 500,
                          lineHeight: "18px",
                          margin: "16px 0",
                        }}
                      >
                        Loading supply requests...
                      </p>
                    ) : requests.length === 0 ? (
                      <p
                        style={{
                          textAlign: "center",
                          fontSize: "13px",
                          fontWeight: 500,
                          lineHeight: "18px",
                          margin: "16px 0",
                        }}
                      >
                        {hasPersistedEncounter
                          ? "No supply requests have been created for this encounter."
                          : "Save this encounter to add supply requests."}
                      </p>
                    ) : (
                      <TableContainer>
                        <Table>
                          <TableHead className="table-header-box">
                            <TableRow>
                              <TableCell
                                width="22%"
                                sx={{ ...tableHeaderCellSx, cursor: "pointer" }}
                                onClick={() =>
                                  setSortOrder(
                                    "supply_request_item",
                                    sortObject.direction === "ascending"
                                      ? "descending"
                                      : "ascending"
                                  )
                                }
                              >
                                <div className="row">
                                  <p className="table-header" style={{ color: "#1E1E1E",fontFamily: "QuicksandBold" }}>Item</p>
                                  {sortObject.field === "supply_request_item" ? (
                                    <span className="sortIndicator">
                                      <img
                                        src={
                                          sortObject.direction === "ascending"
                                            ? images.sort_ascending_src
                                            : images.sort_descending_src
                                        }
                                        width="10"
                                        className="sort-icon"
                                      />
                                    </span>
                                  ) : (
                                    <span className="sortIndicator">
                                      <img
                                        src={images.sort_plain_src}
                                        width="10"
                                        className="sort-icon"
                                      />
                                    </span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell
                                sx={centeredTableHeaderCellSx}
                                width="8%"
                                align="center"
                              >
                                Qty
                              </TableCell>
                              <TableCell sx={tableHeaderCellSx} width="30%">
                                Description
                              </TableCell>
                              <TableCell
                                width="15%"
                                sx={{ ...tableHeaderCellSx, cursor: "pointer" }}
                                onClick={() =>
                                  setSortOrder(
                                    "request_date",
                                    sortObject.direction === "ascending"
                                      ? "descending"
                                      : "ascending"
                                  )
                                }
                              >
                                <div className="row">
                                  <p className="table-header" style={{ color: "#1E1E1E",fontFamily: "QuicksandBold" }}>Request Date</p>
                                  {sortObject.field === "request_date" ? (
                                    <span className="sortIndicator">
                                      <img
                                        src={
                                          sortObject.direction === "ascending"
                                            ? images.sort_ascending_src
                                            : images.sort_descending_src
                                        }
                                        width="10"
                                        className="sort-icon"
                                      />
                                    </span>
                                  ) : (
                                    <span className="sortIndicator">
                                      <img
                                        src={images.sort_plain_src}
                                        width="13"
                                        className="sort-icon"
                                      />
                                    </span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell
                                sx={centeredTableHeaderCellSx}
                                align="center"
                                width="18%"
                              >
                                Status
                              </TableCell>
                              <TableCell
                                align="left"
                                width="13%"
                                sx={tableHeaderCellSx}
                              >
                                <p className="table-header" style={{ color: "#1E1E1E",fontFamily: "QuicksandBold" }}>Tracking Info</p>
                              </TableCell>
                              <TableCell
                                align="center"
                                width="5%"
                                sx={{
                                  ...tableHeaderCellSx,
                                  paddingRight: "16px !important",
                                }}
                                aria-label="Edit"
                              />
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {requests.map((row) => {
                              const isHighlighted =
                                highlightSupplyRequestId != null &&
                                String(row.id) === String(highlightSupplyRequestId);
                              return (
                              <TableRow
                                key={row.id}
                                ref={isHighlighted ? highlightedRowRef : null}
                                sx={{
                                  "& td, & th": { borderBottom: "none" },
                                  ...(isHighlighted && {
                                    backgroundColor: "#FFF7E0",
                                    transition: "background-color 0.3s ease",
                                  }),
                                }}
                              >
                                <TableCell sx={cellSx}>
                                  {row.supply_request_item || "—"}
                                </TableCell>
                                <TableCell sx={cellSx} align="center">
                                  {row.item_quantity ?? "—"}
                                </TableCell>
                                <TableCell sx={cellSx}>
                                  {row.description || "—"}
                                </TableCell>
                                <TableCell sx={cellSx}>
                                  {row.request_date
                                    ? new Date(
                                        row.request_date + "T12:00:00",
                                      ).toLocaleDateString("en-US", {
                                        month: "2-digit",
                                        day: "2-digit",
                                        year: "numeric",
                                      })
                                    : "—"}
                                </TableCell>
                                <TableCell align="center">
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "center",
                                    }}
                                  >
                                    {renderStatusBadge(row)}
                                  </Box>
                                </TableCell>
                                <TableCell sx={cellSx} align="left">
                                  {row.tracking_number || "—"}
                                </TableCell>
                                {/* Edit-icon column (no header). */}
                                <TableCell sx={cellSx} align="center">
                                  <IconButton
                                      onClick={() => handleOpenEditModal(row)}
                                      aria-label="Edit supply request"
                                      disableRipple
                                      sx={{
                                        color: "#424242",
                                        padding: "16px",
                                        borderRadius: 0,
                                        backgroundColor: "transparent",
                                        "&:hover": {
                                          backgroundColor: "transparent",
                                        },
                                      }}
                                    >
                                    <EditIcon
                                      sx={{
                                        fontSize: "24px",
                                      }}
                                    />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {isModalOpen && (
        <AddSupplyRequestModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          encounterId={String(encounterId)}
          payerNames={patientPayerNames}
          availableItems={supplyOptions}
          availableItemObjects={supplyItemObjects}
          onSaveSuccess={() => {
            fetchSupplyRequests();
            setIsModalOpen(false);
          }}
        />
      )}

      {editModalOpen && selectedRequest && (
        <AddSupplyRequestModal
          open={editModalOpen}
          onClose={handleCloseEditModal}
          encounterId={String(encounterId)}
          payerNames={patientPayerNames}
          availableItems={supplyOptions}
          availableItemObjects={supplyItemObjects}
          onSaveSuccess={() => {
            fetchSupplyRequests();
            handleCloseEditModal();
          }}
          isEdit={true}
          existingData={selectedRequest}
        />
      )}

      <Menu
        anchorEl={statusAnchorEl}
        open={Boolean(statusAnchorEl)}
        onClose={handleCloseStatusMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{
          sx: {
            mt: 0.5,
            boxShadow: "0px 4px 12px rgba(0,0,0,0.12)",
          },
        }}
      >
        {STATUS_OPTIONS.map((option) => {
          const optionSx =
            statusStylesByValue[option.value] || statusStylesByValue.new;
          return (
            <MenuItem
              key={option.value}
              onClick={() => {
                if (activeStatusRowId != null) {
                  handleStatusUpdate(activeStatusRowId, option.value);
                }
              }}
              sx={{ py: 0.75 }}
            >
              <Box sx={optionSx}>{option.label}</Box>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default EncounterSuppliesPanel;
